import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  routes,
  type MarketplaceId,
  type StructuredAddressCorrection,
  type MagentoAttempt,
  type MagentoAttemptDecision,
  type MagentoAttemptPreview,
  type SelectMagentoAttemptBody,
} from "@nova-ai/shared"

import { apiFetch } from "../lib/api"

export type OrderLine = {
  sku: string
  name: string | null
  qty: number
  unitPrice: number
  total: number
}

export type ProblemSeverity = "warning" | "critical"

export type OrderProblem = {
  code:
    | "shipped_not_fulfilled"
    | "marketplace_behind"
    | "cancel_mismatch"
    | "magento_unresolved"
    | "magento_push_stuck"
    | "oms_missing"
    | "stage_divergence"
    | "shipment_suspended"
  severity: ProblemSeverity
  systems: string[]
  detail: string
}

export type OmsShipment = {
  shipmentId: string
  omsStatus: string
  trackingCode: string | null
  trackingUrl: string | null
  packedAt: string | null
}

/** Shipment tracking the seller pushed to the marketplace (decoded from the
 *  raw marketplace payload). Lets an operator confirm tracking was forwarded
 *  from our systems to the marketplace. Null until the order ships. */
export type MarketplaceTracking = {
  carrier: string | null
  trackingCode: string | null
  trackingUrl: string | null
}

export type OrderListItem = {
  marketplace: MarketplaceId
  orderId: string
  state: string
  currency: string
  totalPrice: number
  createdAt: string
  baselinkerOrderId: string | null
  baselinkerState: string | null
  magentoOrderId: string | null
  magentoState: string | null
  magentoMatchCount: number
  omsState: string | null
  problems: OrderProblem[]
  problemSeverity: ProblemSeverity | null
  resolvedAt: string | null
  resolutionComment: string | null
  /** Whether a non-stale investigation is stored. The grid does not render it;
   *  it rides the list so the orders-sync read-back can build its problems
   *  delta (see `toOrderAttentionItem`, API side). */
  investigated: boolean
}

export type MagentoMatchCandidate = {
  incrementId: string
  entityId: string
  status: string
  createdAt: string
  grandTotal: number
}

/** The canceled original a spine row was relinked away from (customer-care
 *  cancel-and-replace). Present only on relinked orders. */
export type MagentoReplacedFrom = {
  incrementId: string
  entityId: string
  status: string
  relinkedAt: string
}

export type OrderDetail = {
  marketplace: MarketplaceId
  orderId: string
  state: string
  currency: string
  totalPrice: number
  salesRevenueAmount: number
  shippingPrice: number
  /** Null when the marketplace has no verified realised-commission mapping. */
  commissionTotal: number | null
  refundAmount: number
  lineCount: number
  createdAt: string
  lastUpdatedAt: string
  acceptedAt: string | null
  customerEmail: string | null
  lines: OrderLine[]
  baselinker: { orderId: string | null; state: string | null; syncedAt: string | null }
  magentoDecisions: MagentoAttemptDecision[]
  magento: {
    entityId: string | null
    createdAt: string | null
    manualEntityId: string | null
    selectionVersion: number
    attemptHistory: MagentoAttempt[]
    orderId: string | null
    state: string | null
    orderStatus: string | null
    matchCandidates: MagentoMatchCandidate[] | null
    replacedFrom: MagentoReplacedFrom | null
    syncedAt: string | null
  }
  oms: {
    orderId: string | null
    state: string | null
    trackingUrl: string | null
    trackingCode: string | null
    shipments: OmsShipment[]
  }
  marketplaceTracking: MarketplaceTracking | null
  problems: OrderProblem[]
  resolution: {
    resolvedAt: string | null
    resolvedBy: string | null
    comment: string | null
  }
  /** The agent's recorded interpretation of the problems (`nova orders
   *  record-investigation`), or null when the order was never investigated.
   *  `stale` means the order's problem set changed since it was written. */
  investigation: {
    investigatedAt: string
    summary: string | null
    stale: boolean
  } | null
  addressCorrection: StructuredAddressCorrection | null
  /** Display name of the platform the shop runs on ("Mirakl" / "Octopia"),
   *  derived server-side from the shop's config — the MARKETPLACE journey
   *  step renders it verbatim, so new shops need no dashboard change. */
  platformLabel: string
  urls: {
    /** Deep link into the shop's own platform back office; null when the
     *  shop has no configured template (renders a disabled link). */
    marketplace: string | null
    baselinker: string | null
    magento: string | null
    oms: string | null
  }
}

export type OrdersStateFilter = "all" | "problem" | "resolved"

export type EnrichSummary = {
  scanned: number
  linked: number
  notFound: number
  ambiguous: number
  errors: number
  skippedNoEmail: number
}

export type MarketplaceSyncResult =
  | {
      marketplace: MarketplaceId
      ok: true
      fetched: number
      inserted: number
      updated: number
    }
  | { marketplace: MarketplaceId; ok: false; error: string }

export type MarketplaceSyncSummary = { results: MarketplaceSyncResult[] }

type ListResponse = { orders: OrderListItem[]; nextCursor: string | null }

// The query key is a stable, sorted, comma-joined list of marketplace ids plus
// the state filter, id-search term, and last completed sync timestamp. The
// timestamp lets open dashboards naturally refetch after CLI/cron syncs.
const ordersListKey = (
  marketplaces: MarketplaceId[],
  state: OrdersStateFilter,
  search: string,
  syncVersion: string
) => ["orders", "list", [...marketplaces].sort().join(","), state, search, syncVersion] as const

const orderDetailKey = (marketplace: MarketplaceId, orderId: string) =>
  ["orders", "detail", marketplace, orderId] as const

/** Paginated orders grid. Keyset (cursor) pagination — `getNextPageParam` reads
 *  the opaque `nextCursor` the API returns, so "Load more" walks the full list
 *  without the offset drift that new top-of-list orders would cause. */
export function useOrders(opts: {
  marketplaces?: MarketplaceId[]
  state?: OrdersStateFilter
  search?: string
  syncVersion?: string | null | undefined
}) {
  const marketplaces = opts.marketplaces ?? []
  const state = opts.state ?? "all"
  const search = (opts.search ?? "").trim()
  const syncVersion = opts.syncVersion ?? "unknown"
  return useInfiniteQuery({
    queryKey: ordersListKey(marketplaces, state, search, syncVersion),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      // The API takes a repeated `marketplace` query param, one per
      // marketplace. `apiFetch`'s query helper takes a flat record, so build
      // the URL by hand here.
      const params = new URLSearchParams()
      params.set("state", state)
      for (const m of marketplaces) {
        params.append("marketplace", m)
      }
      if (search) {
        params.set("q", search)
      }
      if (pageParam) {
        params.set("cursor", pageParam)
      }
      const result = await apiFetch<ListResponse>(`${routes.orders.list()}?${params.toString()}`)
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
}

export type OrderCounts = { all: number; problem: number; resolved: number }

/** View counts follow marketplace selection only. The sync version refreshes
 * badges after CLI/cron syncs; search and loaded pages never affect them. */
export function useOrderCounts(marketplaces: MarketplaceId[] = [], syncVersion?: string | null) {
  const sorted = [...marketplaces].sort()
  return useQuery({
    queryKey: ["orders", "counts", sorted.join(","), syncVersion ?? "unknown"] as const,
    queryFn: async () => {
      const params = new URLSearchParams()
      for (const m of sorted) {
        params.append("marketplace", m)
      }
      const qs = params.toString()
      const path = qs ? `${routes.orders.counts()}?${qs}` : routes.orders.counts()
      const result = await apiFetch<OrderCounts>(path)
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

export function useOrder(marketplace: MarketplaceId, orderId: string) {
  return useQuery({
    queryKey: orderDetailKey(marketplace, orderId),
    enabled: !!marketplace && !!orderId,
    queryFn: async () => {
      const result = await apiFetch<OrderDetail>(routes.orders.detail({ marketplace, orderId }))
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

/** Mark a problematic order resolved with a required operator comment. The
 *  order then leaves the `problem` tab and appears under `resolved`. Invalidates
 *  every orders query so the list + detail re-read. */
export function useResolveOrder(marketplace: MarketplaceId, orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (comment: string) => {
      const result = await apiFetch(routes.orders.resolve({ marketplace, orderId }), {
        method: "POST",
        body: { comment },
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["orders"] })
    },
  })
}

/** Reopen a resolved order — clears the resolution so it re-enters `problem`
 *  if the contradiction still holds. */
export function useReopenOrder(marketplace: MarketplaceId, orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await apiFetch(routes.orders.resolve({ marketplace, orderId }), {
        method: "DELETE",
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["orders"] })
    },
  })
}

/** One pipeline stage's live status in the orders-sync job snapshot. Mirror of
 *  the API's `OrdersSyncStageStatus`. `result` is the stage summary — an
 *  {@link EnrichSummary} (baselinker/magento/oms), {@link MarketplaceSyncSummary}
 *  (marketplaces), or {@link DetectSummary} (detect) — once the stage is done. */
export type OrdersSyncStageStatus =
  | { status: "running" }
  | { status: "skipped" }
  | { status: "done"; result: unknown }
  | { status: "partial"; result: unknown; message: string }
  | { status: "error"; message: string }

type OrdersSyncStageKey = "marketplaces" | "baselinker" | "magento" | "oms" | "detect"

export type OrdersSyncJobSnapshot = {
  id: string
  /** `interrupted` = the process died mid-pipeline and boot reconcile flipped
   *  the persisted row (the poller should refresh + clear, not cry "failed"). */
  status: "running" | "finished" | "interrupted"
  /** The persisted job's `updated_at` — the API touches it on every stage
   *  transition + a fixed keep-alive cadence. The poller treats an `updated_at`
   *  frozen past the stale window as `interrupted`: a crash right before a boot
   *  isn't stale yet at that boot's reconcile, so the row can sit falsely
   *  `running` for a window this check bridges. */
  updatedAt: string
  sync: Record<OrdersSyncStageKey, OrdersSyncStageStatus>
  result: unknown | null
  hints?: string[]
}

/** Start the server-owned orders-sync pipeline (marketplace pull → Baselinker →
 * Magento → OMS → Detect) through the shared run-now door. The server derives
 * dashboard provenance. A generic Cloud Run Job owns the work; this client
 * polls the linked workload instead of holding one request socket open. */
type ScheduledOrdersSyncStart = {
  run: { id: string }
  workload: OrdersSyncJobSnapshot | null
}

type ScheduledOrdersSyncRun = {
  status: "queued" | "running" | "succeeded" | "failed" | "interrupted" | "coalesced"
  workload: { id: string } | null
  failureSummary: string | null
}

export async function startOrdersSyncJob(): Promise<ScheduledOrdersSyncStart> {
  const result = await apiFetch<ScheduledOrdersSyncStart>(
    routes.scheduledJobs.runNow({ id: "orders-sync" }),
    {
      method: "POST",
      body: {},
    }
  )
  if (!result.ok) {
    throw new Error(result.error.message)
  }
  return result.value
}

export async function getScheduledOrdersSyncRun(id: string): Promise<ScheduledOrdersSyncRun> {
  const result = await apiFetch<ScheduledOrdersSyncRun>(routes.scheduledJobRuns.detail({ id }))
  if (!result.ok) {
    throw new Error(result.error.message)
  }
  return result.value
}

/** Poll an orders-sync job by id. Returns `null` on a 404: the job is now
 *  persisted, so a restart no longer drops it (it surfaces as `interrupted`
 *  instead) — a 404 means the row was pruned after its retention window or the
 *  id is simply unknown. Either way the caller treats a missing job as
 *  "interrupted" (refresh + clear), not a failure. Throws on any real error. */
export async function getOrdersSyncJob(id: string): Promise<OrdersSyncJobSnapshot | null> {
  const result = await apiFetch<OrdersSyncJobSnapshot>(routes.orders.syncJob({ id }))
  if (!result.ok) {
    if (result.error.kind === "not_found") {
      return null
    }
    throw new Error(result.error.message)
  }
  return result.value
}

export type DetectSummary = {
  scanned: number
  /** Rows that newly became a problem this run. */
  flagged: number
  /** Rows that stopped being a problem this run. */
  cleared: number
  /** Total rows whose stored flag changed. */
  changed: number
}

export type MagentoFreshness = {
  available: boolean
  latestOrderCreatedAt: string | null
  ageSeconds: number | null
  stale: boolean
}

/** Polls how current the Magento read replica is. A stale replica (replication
 *  lag / a bad release) makes recently-pushed orders look `not_found`, so the
 *  grid surfaces this instead of silently mis-flagging. */
export function useMagentoFreshness() {
  return useQuery({
    queryKey: ["orders", "magento-freshness"] as const,
    queryFn: async () => {
      const result = await apiFetch<MagentoFreshness>(routes.orders.magentoFreshness())
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    refetchInterval: 60_000,
  })
}

type LatestOrdersSync = {
  lastSyncedAt: string | null
}

export function useLatestOrdersSync() {
  return useQuery({
    queryKey: ["orders", "latest-sync"] as const,
    queryFn: async () => {
      const result = await apiFetch<LatestOrdersSync>(routes.orders.latestSync())
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    refetchInterval: 30_000,
  })
}

export function useMagentoAttemptPreview(marketplace: MarketplaceId, orderId: string) {
  return useQuery({
    queryKey: ["orders", marketplace, orderId, "magento-attempts"],
    enabled: false,
    queryFn: async () => {
      const result = await apiFetch<MagentoAttemptPreview>(
        routes.orders.magentoAttempts({ marketplace, orderId })
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

export function useSelectMagentoAttempt(marketplace: MarketplaceId, orderId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: SelectMagentoAttemptBody) => {
      const result = await apiFetch(routes.orders.selectMagentoAttempt({ marketplace, orderId }), {
        method: "POST",
        body,
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["orders"] }),
  })
}
